import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, ModalTitle } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';

import { Input } from '../../Input';
import { useAddProviders, useUpdateProvider } from '../../../hooks/useProviders';

type FormData = {
  first_name: string;
  last_name: string;
  npi: string;
};

export type AddOrEditProviderModalProps = {
  open: boolean;
  onClose: () => void;
  existingNpis?: string[];
} & (
  | { mode: 'add'; provider?: never }
  | { mode: 'edit'; provider: { provider_id: string; npi: string; first_name: string; last_name: string } }
);

const AddOrEditProviderModal = ({ open, onClose, mode, existingNpis = [], ...rest }: AddOrEditProviderModalProps) => {
  const provider = 'provider' in rest ? rest.provider : undefined;
  const isEdit = mode === 'edit';

  const successHandler = () => {
    toast.custom(() => <Toast variant="success">{isEdit ? 'Provider updated' : 'Provider added'}</Toast>);
    onClose();
  };
  const errorHandler = () => {
    toast.custom(() => <StandardErrorToast />);
  };

  const addProviders = useAddProviders({ onSuccess: successHandler, onError: errorHandler });
  const updateProvider = useUpdateProvider(provider?.provider_id ?? '', {
    onSuccess: successHandler,
    onError: errorHandler,
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    reset,
    setError,
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: isEdit
      ? { first_name: provider!.first_name, last_name: provider!.last_name, npi: provider!.npi }
      : { first_name: '', last_name: '', npi: '' },
  });

  React.useEffect(() => {
    reset(
      isEdit
        ? { first_name: provider!.first_name, last_name: provider!.last_name, npi: provider!.npi }
        : { first_name: '', last_name: '', npi: '' },
    );
  }, [provider, isEdit, reset]);

  const allFieldsFilled = !!watch('first_name').trim() && !!watch('last_name').trim() && !!watch('npi').trim();

  const mutation = isEdit ? updateProvider : addProviders;

  const onSubmit = (data: FormData) => {
    if (isEdit) {
      updateProvider.mutate({ first_name: data.first_name, last_name: data.last_name });
    } else {
      const trimmedNpi = data.npi.trim();
      if (existingNpis.includes(trimmedNpi)) {
        setError('npi', { message: 'A provider with this NPI already exists.' });
        return;
      }
      addProviders.mutate({
        providers: [{ npi: trimmedNpi, first_name: data.first_name, last_name: data.last_name }],
      });
    }
  };

  if (!open) return null;

  return (
    <Modal onClose={onClose}>
      <ModalTitle>{isEdit ? 'Edit provider information' : 'Add a provider'}</ModalTitle>
      {/* custom ModalBody because the footer buttons are inside the form and can't easily be split out into the ModalFooter component */}
      <div className="flex grow flex-col gap-4 overflow-y-auto px-5 pb-5 pt-4">
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="first_name"
            rules={{ required: true }}
            render={({ field }) => <Input label="First name" {...field} />}
          />

          <Controller
            control={control}
            name="last_name"
            rules={{ required: true }}
            render={({ field }) => <Input label="Last name" {...field} />}
          />

          {isEdit ? (
            <Tooltip
              childNotInteractive
              focusableWrapperClassName="w-full"
              content="To change the NPI, remove this provider and add a new one."
            >
              {/* required for full-width input inside Tooltip */}
              <div className="w-full">
                <Controller
                  control={control}
                  name="npi"
                  render={({ field }) => <Input label="NPI" disabled {...field} />}
                />
              </div>
            </Tooltip>
          ) : (
            <Controller
              control={control}
              name="npi"
              rules={{ required: true, pattern: /^\d{10}$/ }}
              render={({ field }) => (
                <Input
                  label="NPI"
                  hasError={!!errors.npi}
                  errorMessage={errors.npi?.message || 'This NPI is invalid. Please check again.'}
                  {...field}
                />
              )}
            />
          )}

          <ButtonRow>
            <Button type="button" onClick={onClose} variant="tertiary" size="sm" disabled={mutation.isPending}>
              Back
            </Button>
            <Tooltip
              content="Complete all fields to continue"
              childNotInteractive
              disabled={allFieldsFilled && (!isEdit || isDirty)}
            >
              <Button
                type="submit"
                disabled={!allFieldsFilled || (isEdit && !isDirty)}
                isLoading={mutation.isPending}
                size="sm"
              >
                Save
              </Button>
            </Tooltip>
          </ButtonRow>
        </form>
      </div>
    </Modal>
  );
};

export default AddOrEditProviderModal;
